import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { analyzeAPI, simulationAPI } from '../lib/api';
import { runJob } from '../lib/jobs';
import { t } from '../lib/i18n';

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

// Server job status -> progress step index (see STEPS in UploadCard)
export const STATUS_STEP = { queued: 0, analyzing: 1, prompting: 2, generating: 3, saving: 4, done: 4 };

const toJpegSrc = (b64) => (b64 ? `data:image/jpeg;base64,${b64}` : null);

/**
 * Simulation workflow: pick a photo, run the background job with real progress,
 * or load a previously saved simulation by session id.
 */
export const useSimulationJob = (language, savedSessionId = null) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(Boolean(savedSessionId));
  const [progressStep, setProgressStep] = useState(0);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const fileInputRef = useRef(null);

  const reset = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setNotFound(false);
    setProgressStep(0);
  }, []);

  useEffect(() => {
    if (!savedSessionId) {
      setLoadingSaved(false);
      return;
    }
    let cancelled = false;
    setLoadingSaved(true);
    setNotFound(false);
    simulationAPI.get(savedSessionId)
      .then(res => {
        if (cancelled) return;
        const d = res.data;
        setResult({
          sessionId: d.session_id,
          analysis: d.analysis,
          beforeSrc: toJpegSrc(d.before_image),
          afterSrc: toJpegSrc(d.after_image),
          createdAt: d.created_at,
          saved: true,
        });
      })
      .catch(err => {
        if (cancelled) return;
        setNotFound(true);
        toast.error(err.response?.status === 404 ? t('simulationNotFound', language) : (language === 'fr' ? 'Erreur de chargement' : 'Loading error'));
      })
      .finally(() => { if (!cancelled) setLoadingSaved(false); });
    return () => { cancelled = true; };
  }, [savedSessionId, language]);

  const selectFile = (file) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error(language === 'fr' ? 'Fichier trop volumineux (max 10MB)' : 'File too large (max 10MB)');
      return;
    }
    setSelectedFile(file);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const simulate = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setProgressStep(0);
    setResult(null);
    try {
      const data = await runJob(() => analyzeAPI.simulate(selectedFile, language), {
        onStatus: (status) => setProgressStep(STATUS_STEP[status] ?? 0),
      });
      setResult({
        sessionId: data.session_id,
        analysis: data.analysis,
        beforeSrc: toJpegSrc(data.before_image) || preview,
        afterSrc: toJpegSrc(data.after_image),
        createdAt: new Date().toISOString(),
        saved: false,
      });
      toast.success(language === 'fr' ? 'Simulation g\u00e9n\u00e9r\u00e9e !' : 'Simulation generated!');
    } catch (err) {
      if (err.response?.data?.detail === 'photo_consent_required') toast.error(t('consentRequired', language));
      else if (err.code === 'job_timeout') toast.error(t('jobTimeout', language));
      else toast.error(language === 'fr' ? 'Erreur lors de la simulation' : 'Error during simulation');
    } finally {
      setLoading(false);
    }
  };

  return { selectedFile, preview, loading, loadingSaved, progressStep, result, notFound, fileInputRef, selectFile, simulate, reset };
};
